/**
 * Module + page matching for navigation search.
 *
 * This is the shared engine behind every "find me a module or a page" surface:
 * the module picker (header switcher, sidebar dropdown, `ModulePickerSelect`)
 * and the global search bar / Cmd+K palette. Both must rank the same way — a
 * query that finds "Warehouses" in the picker has to find it in global search
 * too — so the scoring, synonym handling and cross-module rewriting live here
 * once rather than being reimplemented per surface.
 *
 * Matching itself comes from {@link searchScore} (multi-token, singular/plural
 * folding, small-typo tolerance); this module adds the navigation-specific
 * rules layered on top of it.
 */

import type { ModuleConfig, NavItem } from '../config/modules.config';
import { searchScore } from './searchScore';

/** Resolves an i18n key to its display string (page label, section, description). */
export type TranslateKey = (key: string) => string;

/** Resolves a module's display name (persona-aware — see `useModuleName`). */
export type ResolveModuleName = (module: ModuleConfig) => string;

/** A page (nav item) that matched the query, with the module that owns it. */
export interface ModulePageMatch {
  module: ModuleConfig;
  navItem: NavItem;
  /** Relevance score (higher first) — see {@link searchScore}. */
  score: number;
}

/** Modules that matched the query, plus every candidate's raw score. */
export interface ModuleMatchResult {
  /** Matching modules, most relevant first. */
  modules: ModuleConfig[];
  /** Score for every candidate module (0 = no match) — for regrouping/sorting. */
  scoreById: Map<string, number>;
}

/**
 * Exact takes preference over fuzzy. searchScore tiers a typo/fuzzy match
 * (~0.5) below a stem match (~0.7–0.9) below an exact substring/prefix match
 * (≥1). When any module matches at the stem-or-better tier, drop modules that
 * only matched in the fuzzy/typo tier — e.g. "Contracts" for "contacts" must
 * not sit above the exact "Contacts" page (modules render above pages, so a
 * surviving fuzzy module would outrank an exact page). Fuzzy matches still
 * surface as a fallback when nothing stronger matched (genuine typos like
 * "accounitng" → Accounting).
 */
const STRONG_MATCH = 0.7;

/**
 * Pages are only searched once the query is at least this long — a single
 * character matches almost every page and would bury the module results.
 */
const MIN_PAGE_QUERY_LENGTH = 2;

/**
 * Match modules by name and search synonyms.
 *
 * Synonyms (e.g. "CRM" → pipeline) are language-independent search aliases
 * folded into searchScore's keyword haystack, so a module is findable by an
 * alternative name regardless of the UI language.
 *
 * An empty query matches every module (searchScore's "show everything"
 * contract) — callers that render a resting state handle that themselves.
 */
export function matchModules(
  modules: ModuleConfig[],
  query: string,
  getModuleName: ResolveModuleName,
): ModuleMatchResult {
  // Score every module once (reused for both filtering and sorting).
  const scoreById = new Map<string, number>();
  for (const module of modules) {
    scoreById.set(module.id, searchScore(getModuleName(module), query, module.searchSynonyms));
  }

  const scored = modules.filter((m) => (scoreById.get(m.id) ?? 0) > 0);
  const hasStrong = scored.some((m) => (scoreById.get(m.id) ?? 0) >= STRONG_MATCH);
  const filtered = hasStrong
    ? scored.filter((m) => (scoreById.get(m.id) ?? 0) >= STRONG_MATCH)
    : scored;

  const sorted = [...filtered].sort(
    (a, b) => (scoreById.get(b.id) ?? 0) - (scoreById.get(a.id) ?? 0),
  );

  return { modules: sorted, scoreById };
}

/**
 * Match pages (nav items) within modules, most relevant first.
 *
 * Each page is scored against a composed haystack — module name + section +
 * page label + description — so a mixture of module/submodule and page words
 * (e.g. "product categories", "master data categories") finds the page, and
 * singular/plural/typo tolerance comes for free from searchScore.
 *
 * Access is the CALLER's responsibility at both levels: it passes only the
 * modules the viewer may open, and an `isVisible` predicate for the nav items
 * within them (an admin-only page can live inside a module everyone may open).
 * The predicate is required rather than optional so a new surface cannot
 * silently list pages its viewer may not see — pass
 * {@link isNavItemVisible} bound to the viewer's access.
 *
 * @param modules - every candidate module (the same list passed to {@link matchModules})
 * @param matchedModuleIds - ids of the modules that matched, so their pages
 *   aren't repeated below the module row that already represents them
 * @param isVisible - per-nav-item access gate (see `utils/navItemAccess`)
 */
export function matchModulePages(
  modules: ModuleConfig[],
  query: string,
  matchedModuleIds: Set<string>,
  getModuleName: ResolveModuleName,
  translate: TranslateKey,
  isVisible: (navItem: NavItem) => boolean,
): ModulePageMatch[] {
  if (!query || query.length < MIN_PAGE_QUERY_LENGTH) return [];

  const matches: ModulePageMatch[] = [];

  for (const module of modules) {
    const moduleName = getModuleName(module);
    // When a module matched by its own NAME, its pages would be redundant with
    // the module row (e.g. the "Pipelines" page under the "Pipeline" module) —
    // skip them entirely. But when it matched only via a SYNONYM (e.g.
    // "warehouses" hits Inventory through the "warehouse" synonym while the
    // name "Inventory" does not match), a page whose own label directly matches
    // is a distinct, valuable result and must still surface — so fall through
    // and require a strong page-label match below.
    const moduleMatched = matchedModuleIds.has(module.id);
    const moduleMatchedByName = moduleMatched && searchScore(moduleName, query) > 0;
    if (moduleMatchedByName) continue;

    for (const navItem of module.navItems) {
      // Item-level gate (requiresAdmin / requiresSysadmin / connection /
      // persona). Applied before scoring so a page the viewer cannot open is
      // never ranked, counted or offered.
      if (!isVisible(navItem)) continue;
      const label = translate(navItem.labelKey);
      // navItem.searchSynonyms are language-independent aliases for the page
      // (e.g. "estimates" → Quotes), folded into the keyword haystack.
      // Findability is driven by the full haystack (so a page is still found
      // by words drawn from its module/section/description). Ranking, however,
      // is driven primarily by the page LABEL: a page whose own name matches
      // (e.g. "Contacts" for "contacts") must outrank one that merely mentions
      // the word in its description (e.g. Pipeline > Leads — "Contacts in the
      // early sales journey"). Without this they'd tie on the same
      // non-first-word tier (the module name is always haystack word 0) and
      // sort by nav order. The label score is weighted above any single-field
      // haystack score (max ~3), giving a clean label-primary, haystack-tiebreak
      // ordering.
      const labelScore = searchScore(label, query, navItem.searchSynonyms);
      // A page within a module that matched only by a SYNONYM (its own name does
      // not match the query) is surfaced only when its OWN label is a strong
      // (stem-or-better) match — so a direct hit like "Warehouses" appears
      // without flooding the results with every other page of that module.
      if (moduleMatched && labelScore < STRONG_MATCH) continue;
      const haystack = [
        moduleName,
        navItem.section ? translate(navItem.section) : '',
        label,
        navItem.descriptionKey ? translate(navItem.descriptionKey) : '',
      ]
        .filter(Boolean)
        .join(' ');
      const haystackScore = searchScore(haystack, query, navItem.searchSynonyms);
      if (haystackScore > 0) {
        matches.push({ module, navItem, score: labelScore * 10 + haystackScore });
      }
    }
  }

  // Prefer the canonical master-data page over a cross-module shortcut.
  // Cross-module shortcut nav items (navItem.crossModule set) are secondary
  // pointers to a canonical page owned by another module — e.g. inventory's
  // "Products" and pipeline's "Contacts"/"Organizations" all point at
  // master_data. When such a shortcut matches, rewrite it to the canonical
  // page so searching "products"/"contacts" surfaces the master-data page
  // (the main location) rather than the module shortcut. This matters because
  // master_data matches the same queries by module synonym, which suppresses
  // its own pages — leaving the shortcut as the only page match; rewriting it
  // restores the canonical page. The canonical page shares the shortcut's
  // trailing path segment (.../products, .../contacts). If the canonical
  // module or page can't be resolved, the shortcut is left intact.
  const pathLeaf = (path: string) => path.split('/').filter(Boolean).pop() ?? path;
  const moduleById = new Map(modules.map((m) => [m.id, m]));
  const rewritten = matches.map((match) => {
    if (!match.navItem.crossModule) return match;
    const canonicalModule = moduleById.get(match.navItem.crossModule);
    // The canonical page is itself gated — never rewrite a shortcut the viewer
    // may follow into a canonical page they may not see.
    const canonicalNav = canonicalModule?.navItems.find(
      (n) => !n.crossModule && pathLeaf(n.path) === pathLeaf(match.navItem.path) && isVisible(n),
    );
    if (canonicalModule && canonicalNav) {
      return { module: canonicalModule, navItem: canonicalNav, score: match.score };
    }
    return match;
  });

  // Deduplicate by target page — a rewritten shortcut can now coincide with an
  // independently-matched canonical page (or another shortcut to the same
  // page). Keep the strongest score for each target.
  const byPath = new Map<string, ModulePageMatch>();
  for (const match of rewritten) {
    const key = `${match.module.id} ${match.navItem.path}`;
    const existing = byPath.get(key);
    if (!existing || match.score > existing.score) byPath.set(key, match);
  }

  // Sort by relevance: strongest matches first.
  return [...byPath.values()].sort((a, b) => b.score - a.score);
}
